import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Loader2, BadgeCheck, Search } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SmartImage from '../components/SmartImage';
import api, { mediaUrl } from '../lib/api';
import { typeLabel } from '../lib/centerOptions';

const VARIANTS = {
  community: {
    eyebrow: 'Places of Devotion',
    title: ['Ashrams, Centers &', 'Communes'],
    subtitle: 'Discover ashrams, meditation centers and communes connected to the Osho Universal community around the world.',
    tabs: [['', 'All'], ['ashram', 'Ashram'], ['center', 'Center'], ['commune', 'Commune']],
    allowedTypes: ['ashram', 'center', 'commune'],
  },
  temples: {
    eyebrow: 'Sacred Spaces',
    title: ['Our', 'Temples'],
    subtitle: 'Visit the temples affiliated with Osho Universal — spaces of silence, devotion and celebration.',
    tabs: null,
    fixedType: 'temple',
    allowedTypes: ['temple'],
  },
};

export const CenterCard = ({ c }) => (
  <Link to={`/centers/${c.slug}`} data-testid={`center-card-${c.slug}`}
    className="group bg-white/70 rounded-3xl overflow-hidden border border-rose/40 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 block">
    <SmartImage src={mediaUrl(c.cover_image)} alt={c.name} className="aspect-[4/3]">
      <div className="absolute top-3 left-3 z-10 rounded-full bg-cream/90 text-mahogany text-[11px] uppercase tracking-widest px-3 py-1">{typeLabel(c.type)}</div>
      {c.is_verified && (
        <div className="absolute top-3 right-3 z-10 rounded-full bg-green-600 text-white text-[11px] px-2.5 py-1 flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5" /> Verified</div>
      )}
    </SmartImage>
    <div className="p-6">
      <h3 className="font-serif-display text-burgundy text-2xl leading-tight mb-2 group-hover:text-rose-italic transition-colors">{c.name}</h3>
      {c.short_description && <p className="text-sm text-burgundy/80 line-clamp-2 mb-3">{c.short_description}</p>}
      {(c.location?.city || c.location?.country) && (
        <div className="flex items-center gap-2 text-sm text-burgundy/80"><MapPin className="w-4 h-4 text-rose-italic" /> {[c.location?.city, c.location?.country].filter(Boolean).join(', ')}</div>
      )}
      {c.programs_offered?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {c.programs_offered.slice(0, 3).map((p) => <span key={p} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-soft text-mahogany">{p}</span>)}
        </div>
      )}
    </div>
  </Link>
);

const CentersDirectory = ({ variant = 'community' }) => {
  const cfg = VARIANTS[variant] || VARIANTS.community;
  const [type, setType] = useState('');
  const [q, setQ] = useState('');
  const [verified, setVerified] = useState('');
  const [country, setCountry] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { setType(''); setQ(''); setVerified(''); setCountry(''); }, [variant]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    const activeType = cfg.fixedType || type;
    if (activeType) params.type = activeType;
    if (q) params.q = q;
    if (verified) params.verified = verified === 'yes';
    api.get('/centers', { params })
      .then((res) => {
        let data = res.data || [];
        if (!activeType) data = data.filter((c) => cfg.allowedTypes.includes(c.type));
        setItems(data);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [variant, type, q, verified]); // eslint-disable-line react-hooks/exhaustive-deps

  const countries = useMemo(
    () => Array.from(new Set(items.map((c) => c.location?.country).filter(Boolean))).sort(),
    [items]
  );
  const visible = useMemo(
    () => (country ? items.filter((c) => c.location?.country === country) : items),
    [items, country]
  );

  return (
    <div className="App">
      <Header />
      <main className="bg-cream min-h-[60vh]">
        <section className="max-w-[1300px] mx-auto px-6 lg:px-10 py-16">
          <div className="text-center">
            <div className="eyebrow mb-3">{cfg.eyebrow}</div>
            <h1 className="font-serif-display text-4xl md:text-5xl text-burgundy">{cfg.title[0]} <span className="font-italic-display text-rose-italic">{cfg.title[1]}</span></h1>
            <p className="mt-4 text-mahogany/80 max-w-2xl mx-auto">{cfg.subtitle}</p>
            <Link to="/dashboard/centers" data-testid="register-center-cta" className="pill-btn mt-6 inline-flex">
              <MapPin className="w-4 h-4" /> {variant === 'temples' ? 'Add Your Temple' : 'Register Your Center / Ashram'}
            </Link>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-3 mt-8">
            {cfg.tabs && (
              <div className="inline-flex flex-wrap justify-center rounded-full bg-white/70 border border-rose/50 p-1" data-testid="center-type-tabs">
                {cfg.tabs.map(([key, label]) => (
                  <button key={key} onClick={() => setType(key)} data-testid={`center-tab-${key || 'all'}`}
                    className={`px-5 py-2 rounded-full text-sm transition-colors ${type === key ? 'bg-mahogany text-cream' : 'text-mahogany/70 hover:bg-rose-soft/40'}`}>{label}</button>
                ))}
              </div>
            )}
            <div className="relative">
              <Search className="w-4 h-4 text-mahogany/50 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name…" data-testid="center-search"
                className="bg-white/70 rounded-full border border-rose/50 pl-9 pr-4 py-2 text-burgundy focus:outline-none" />
            </div>
            <select value={verified} onChange={(e) => setVerified(e.target.value)} data-testid="center-verified-filter"
              className="bg-white/70 rounded-full border border-rose/50 px-4 py-2 text-burgundy focus:outline-none">
              <option value="">All</option>
              <option value="yes">Verified only</option>
            </select>
            <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="center-location-filter"
              className="bg-white/70 rounded-full border border-rose/50 px-4 py-2 text-burgundy focus:outline-none">
              <option value="">All locations</option>
              {countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="flex justify-center mt-16"><Loader2 className="w-8 h-8 animate-spin text-mahogany" /></div>
          ) : visible.length === 0 ? (
            <div className="mt-16 text-center text-mahogany/60" data-testid="centers-empty">Nothing to show yet. Please check back soon.</div>
          ) : (
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="centers-grid">
              {visible.map((c) => <CenterCard key={c.id} c={c} />)}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CentersDirectory;
